import { ApiResult, HttpClient } from "../http.js";

export type SupportTicketType = "bug" | "feature" | "enhancement";

export interface SupportTicketDto {
  id: string;
  type: SupportTicketType;
  title: string;
  description: string;
  githubIssueNumber: number;
  githubIssueUrl: string;
  status: "open" | "closed";
  stateReason?: string | null;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
}

export interface CreateSupportTicketPayload {
  type: SupportTicketType;
  title: string;
  description: string;
  stepsToReproduce?: string | null;
  expectedBehavior?: string | null;
  actualBehavior?: string | null;
  browser?: string | null;
  device?: string | null;
}

/**
 * A roadmap entry, read live from GitHub. Not persisted — the roadmap is a view of the
 * issue tracker, not our data. No body: issue bodies carry the reporter's name, email
 * and company, and the roadmap is shown to every customer.
 */
export interface RoadmapIssueDto {
  number: number;
  title: string;
  state: "open" | "closed";
  labels: string[];
  url: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Support tickets and the public roadmap, backed by GitHub Issues.
 *
 * Replaces the Firebase `submitGitHubTicket` and `getRoadmapIssues` callables plus the
 * direct Firestore read behind "Mine tickets". The first two opened with
 * `if (!request.auth) throw unauthenticated`, and this app has carried no Firebase
 * session since the RsConnect cutover — so every submission failed and the ticket list
 * was permanently empty.
 */
export class SupportApi {
  constructor(private readonly http: HttpClient) {}

  createTicket(payload: CreateSupportTicketPayload): Promise<ApiResult<SupportTicketDto>> {
    return this.http.post<SupportTicketDto>("/api/support/tickets", { body: payload });
  }

  myTickets(): Promise<ApiResult<SupportTicketDto[]>> {
    return this.http.get<SupportTicketDto[]>("/api/support/tickets");
  }

  roadmap(): Promise<ApiResult<RoadmapIssueDto[]>> {
    return this.http.get<RoadmapIssueDto[]>("/api/support/roadmap");
  }
}
